import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Skill } from '@app/onboarding/onboarding.model';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { OnboardingConfigService } from '@app/onboarding/services/onboarding-config.service';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { OnboardingService } from '@app/onboarding/services/onboarding.service';
import { ReviewSkillsService } from '@app/onboarding/services/review-skills.service';
import { DataColumn } from '@app/shared/components/data-table/data-table';
import { AuthService } from '@app/shared/services/auth.service';
import { ModalService } from '@app/shared/services/modal.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AreYouSureRatingModalComponent } from '../are-you-sure-rating-modal/are-you-sure-rating-modal.component';
import { FooterButtonStates } from '../onboarding-footer/onboarding-footer.model';

@Component({
  selector: 'dgx-review-skills',
  templateUrl: './review-skills.component.html',
  styleUrls: ['./review-skills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewSkillsComponent implements OnInit, OnDestroy {
  public templatePrefix: string;
  public showSuggestions = false;
  public i18n = this.translateService.instant([
    'OnboardingFooter_PreviousStep',
    'OnboardingFooter_NextStep',
    'Onboarding_AssignedSkillsHeader',
    'Onboarding_AssignedSkillsInformationTooltip',
    'Onboarding_AssignedSkillsWarning',
    'Onboarding_AssignedSkillsError',
    'Onboarding_FocusSkillTooltip',
    'Onboarding_SeeSuggestedSkills',
    'Onboarding_RoleSkill',
    'Onboarding_ResumeSkill',
    'Onboarding_ProfileSkill',
    'Onboarding_AddedSkill',
    'dgTagRating_RemoveFocusSkill',
    'dgTagRating_AddFocusSkill',
    'dgTagRating_SelfAssessment',
  ]);
  public displaySkills;
  @ViewChild('isFocusSkillColumnTemplate', { static: true })
  public isFocusSkillColumnTemplate: TemplateRef<any>;
  @ViewChild('skillNameColumnTemplate', { static: true })
  public skillNameColumnTemplate: TemplateRef<any>;
  @ViewChild('selfRatingColumnTemplate', { static: true })
  public selfRatingColumnTemplate: TemplateRef<any>;
  @ViewChild('sourceTemplate', { static: true })
  public sourceTemplate: TemplateRef<any>;
  @ViewChild('removeTemplate', { static: true })
  public removeTemplate: TemplateRef<any>;
  public columns: DataColumn[] = [];
  public isLoading: boolean = true;
  public hasNoResults: boolean = true;
  public skillsDisplay: Skill[] = [];
  public displayedSimilarSkills: Observable<Skill[]> =
    this.onboardingStateService.displayedSimilarTags$;
  public onboardingRateSkillsMandatory: boolean;

  public readonly MINIMUM_MANDATORY_SKILL_COUNT: number = 3;
  private footerButtonStates: FooterButtonStates;
  private selfRatingsCount = 0;
  private listener: Subscription;

  constructor(
    private translateService: TranslateService,
    private onboardingConfigService: OnboardingConfigService,
    private onboardingStateService: OnboardingStateService,
    private cdr: ChangeDetectorRef,
    private tagsService: TagsService,
    private trackerService: TrackerService,
    private tagRatingService: TagRatingService,
    private onboardingService: OnboardingService,
    private modalService: ModalService,
    private router: Router,
    private reviewSkillsService: ReviewSkillsService,
    private authService: AuthService
  ) {}

  public get onboarding_ReviewSkillsHeader(): string {
    return this.translateService.instant(
      this.onboardingRateSkillsMandatory
        ? `Onboarding_ReviewSkillsHeader`
        : `Onboarding_ReviewOptionalSkillsHeader`
    );
  }

  public get onboarding_ReviewSkillsDescription(): string {
    return this.translateService.instant(
      this.onboardingRateSkillsMandatory
        ? `Onboarding_ReviewSkillsDescription`
        : `Onboarding_ReviewOptionalSkillsDescription`,
      {
        orgName: this.onboardingConfigService.organizationNameInOnboarding,
      }
    );
  }

  public ngOnInit(): void {
    this.onboardingRateSkillsMandatory =
      this.authService.authUser?.onboardingRateSkillsMandatory ?? true;

    // setup default states
    this.footerButtonStates = {
      currentStep: OnboardingStep.review,
      disableNextStep:
        this.onboardingRateSkillsMandatory &&
        this.selfRatingsCount < this.MINIMUM_MANDATORY_SKILL_COUNT,
      previousButton: this.i18n.OnboardingFooter_PreviousStep,
      nextButton: this.i18n.OnboardingFooter_NextStep,
      disabledStatus: this.footerButtonStatesDisableStatus(),
    };
    this.onboardingConfigService.footerButtonStateChange(
      this.footerButtonStates
    );
    this.onboardingStateService.currentStepChange(OnboardingStep.review);
    this.trackerService.setLocation('Onboarding');

    // Also emit when we see the userTagsModified event
    this.listener = this.tagsService.userTagsModified.subscribe(() =>
      this.onboardingService.refresh$.next(null)
    );

    // get list of skills from user role, role assigned, and resume and create a onboardingUserAddedTags
    this.reviewSkillsService.createOnboardingAddedTags();

    this.setupDisplaySkills();
    this.onboardingConfigService.config.subscribe((config) => {
      this.templatePrefix = config.templatePrefix;
      let steps;
      if (this.onboardingStateService.trackerSteps) {
        steps = this.onboardingConfigService.updateActiveTrackerStep(
          this.onboardingStateService.trackerSteps,
          OnboardingStep.review
        );
        this.onboardingStateService.updateTrackerStep(steps);
      } else {
        this.onboardingStateService.updateTrackerStep(
          this.onboardingConfigService.getTrackerSteps(
            this.templatePrefix,
            OnboardingStep.review,
            false
          )
        );
      }
      this.cdr.detectChanges();
    });
    this.setupReviewColumns();
  }

  public ngOnDestroy() {
    if (this.listener) {
      this.listener.unsubscribe();
    }
  }

  /* Open the Add Target Level modal.
   *
   * @param event
   * @param skill - The skill to adjust the target level of.
   * @param addSkill - will present a dialog allowing user to add the skill with or without a rating
   */
  public handleAddTargetLevel(
    event: Event,
    tag: Skill,
    addSkill: boolean = false
  ): Observable<number> {
    event.stopPropagation();
    let ratingLevel = 1;
    let emptyRating = true;

    const modalTitle = addSkill
      ? this.translateService.instant('dgTagRating_AddToMySkills', {
          tagName: tag.title,
        })
      : this.translateService.instant('dgTagRating_RatingTitleFormat', {
          tagName: tag.title,
        });
    const selfRatingSkill = tag.ratings?.find(
      (r) => r.type === InternalTagRatingTypes.self
    );

    if (selfRatingSkill) {
      ratingLevel = +selfRatingSkill.level;
      emptyRating = false;
    } else {
      ratingLevel = 1;
    }

    const tagCopy = {
      ...tag,
      rating: { level: '' + ratingLevel } as TagsApi.UserTagRating,
    };
    const tagRatingModal = this.tagRatingService
      .openRatingModal({
        event,
        title: modalTitle,
        tag: tagCopy,
        ratingType: InternalTagRatingTypes.self,
        emptyRating,
        initialValue: +tagCopy.rating.level,
        instructionsText: tag.isFollowing
          ? undefined
          : 'PersonalSkillView_AddSkillInstructions',
        addNewSkill: addSkill,
      })
      .pipe(
        tap(({ level }) => {
          if ((level as unknown as number) === 1) {
            const inputs = {
              tag: tag,
              modalTitle: modalTitle,
              addSkill: addSkill,
            };
            this.modalService
              .show<any>(AreYouSureRatingModalComponent, {
                inputs,
              })
              .subscribe();
          } else {
            this.reviewSkillsService.selfRating(
              tag,
              level as unknown as number
            );
          }
        }),
        // for now, swallow and return no results
        catchError(() => [])
      );
    tagRatingModal.subscribe();
    return tagRatingModal;
  }

  public removeTag(item): void {
    this.displaySkills = this.displaySkills.filter(
      (tag) => tag.resourceId != item.resourceId
    );

    if (
      item.ratings?.find(
        (rating) => rating.type === InternalTagRatingTypes.self
      )?.level
    ) {
      this.selfRatingsCount--;
      this.footerButtonStates.disabledStatus =
        this.footerButtonStatesDisableStatus();
    }

    if (item.source === this.i18n.Onboarding_AddedSkill) {
      this.onboardingService.removeAddSkill(item);
    } else {
      this.onboardingService.removeOnboardingUserAddedTag(item);
    }
    this.tagsService.removeUserTag(item).subscribe();
  }

  public toggleFocused($event, tag): void {
    // need both if using ui-sref
    $event.preventDefault();
    $event.stopPropagation();
    tag.requestingUserIsFocused = tag.isFocused;

    this.tagsService.toggleUserInterestFocus(tag).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  public toggleSuggestions() {
    this.showSuggestions = !this.showSuggestions;
  }

  public getFocusSkillI18n(isFocused: boolean): string {
    return isFocused
      ? this.i18n.dgTagRating_RemoveFocusSkill
      : this.i18n.dgTagRating_AddFocusSkill;
  }

  public getSelfRatingLevel(skill: Skill): number {
    return parseInt(
      skill.ratings?.find(
        (rating) => rating.type === InternalTagRatingTypes.self
      )?.level
    );
  }

  private setupDisplaySkills(): void {
    combineLatest([
      this.onboardingService.userTags$,
      this.onboardingStateService.addedTags$,
      this.onboardingStateService.onboardingUserAddedTags$,
    ])
      .pipe(
        map(([userTags, addedTags, onboardingUserAddedTags]) => {
          const addedSearchSuggestedTagsUpdate: Skill[] = addedTags?.map(
            (obj) => ({
              ...obj,
              ratings: obj.ratings ? obj.ratings : [],
            })
          );

          const profileSkills: Skill[] = userTags?.map((obj) => ({
            ...obj,
            source: this.i18n.Onboarding_ProfileSkill,
          }));
          return [
            profileSkills,
            addedSearchSuggestedTagsUpdate,
            onboardingUserAddedTags,
          ];
        })
      )
      .subscribe(([userTags, addedTags, roleSkills]) => {
        this.selfRatingsCount = this.getRatingsCount(
          userTags,
          addedTags,
          roleSkills
        );

        this.footerButtonStates.disabledStatus =
          this.footerButtonStatesDisableStatus();
        // filter out duplicates
        let searchSkills: Skill[] = [...userTags];

        if (roleSkills) {
          const searchSkillsIds = new Set(searchSkills.map((d) => d.tagId));
          searchSkills = [
            ...searchSkills,
            ...roleSkills?.filter((d) => !searchSkillsIds.has(d.tagId)),
          ];
        }
        if (addedTags) {
          const searchSkillsIds = new Set(searchSkills.map((d) => d.tagId));
          searchSkills = [
            ...addedTags?.filter((d) => !searchSkillsIds.has(d.tagId)),
            ...searchSkills,
          ];
        }

        this.onboardingStateService.updateProfileSkills(userTags);
        this.displaySkills = searchSkills;
        // Setup similar skills
        this.onboardingService.updateSimilarSkills();

        // update footer states
        this.updateFooterStates();
        this.isLoading = false;
        this.cdr.detectChanges();
      });
  }

  private getRatingsCount(
    userTags: Skill[],
    addedTags: Skill[],
    roleSkills: Skill[]
  ): number {
    let ratingsCount = 0;
    userTags?.map((tag) => {
      tag.ratings?.map((rating) => {
        if (rating.type == InternalTagRatingTypes.self) {
          ratingsCount++;
        }
      });
    });
    addedTags?.map((tag) => {
      tag.ratings?.map((rating) => {
        if (rating.type == InternalTagRatingTypes.self) {
          ratingsCount++;
        }
      });
    });
    roleSkills?.map((tag) => {
      tag.ratings?.map((rating) => {
        if (rating.type == InternalTagRatingTypes.self) {
          ratingsCount++;
        }
      });
    });
    return ratingsCount;
  }

  private updateFooterStates(): void {
    if (this.router.url.includes('/review')) {
      this.footerButtonStates = {
        currentStep: OnboardingStep.review,
        disableNextStep:
          this.onboardingRateSkillsMandatory &&
          this.selfRatingsCount < this.MINIMUM_MANDATORY_SKILL_COUNT,
        previousButton: this.i18n.OnboardingFooter_PreviousStep,
        nextButton: this.i18n.OnboardingFooter_NextStep,
        disabledStatus: this.footerButtonStatesDisableStatus(),
      };
      this.onboardingConfigService.footerButtonStateChange(
        this.footerButtonStates
      );
    }
  }

  private footerButtonStatesDisableStatus(): string {
    if (this.onboardingRateSkillsMandatory) {
      return this.translateService.instant(
        'OnboardingFooter_RatingDisabledStatus',
        { ratings: this.selfRatingsCount }
      );
    } else {
      return null;
    }
  }

  public getTooltip(skill: Skill) {
    return this.getAriaLabel(skill);
  }

  public getAriaLabel(skill: Skill) {
    let level: number = this.getSelfRatingLevel(skill);
    if (Number.isNaN(level)) {
      return this.translateService.instant('dgTagRating_SelfAssessment');
    }
    return `${this.translateService.instant(
      'dgTagRating_SelfAssessment'
    )}: ${level}`;
  }

  private setupReviewColumns(): void {
    this.columns = [
      {
        label: '',
        icon: {
          icon: 'question-mark-circle',
          iconClass: 'action-options__button',
          size: 'small',
        },
        headClasses: 'l_w-min guts-p-h-1-half',
        bodyClasses: 'l_w-min guts-p-h-1-half',
        tooltip: this.i18n.Onboarding_FocusSkillTooltip,
        template: this.isFocusSkillColumnTemplate,
      },
      {
        label: 'Core_Skill',
        template: this.skillNameColumnTemplate,
      },
      {
        label: 'dgTagRating_SelfAssessment',
        headClasses: 'no-wrap center-text',
        template: this.selfRatingColumnTemplate,
      },
      {
        label: 'Core_Source',
        headClasses: 'no-mobile',
        bodyClasses: 'no-mobile',
        template: this.sourceTemplate,
      },
      {
        label: 'Core_Remove',
        headClasses: 'center-text',
        bodyClasses: 'center-text',
        template: this.removeTemplate,
      },
    ];
  }
}
